<template>
    <div>
        <div v-if="!loaded" class="enqContent d-flex align-items-center">
            <strong>{{ __("Loading...") }}</strong>
            <div
                class="spinner-border ml-auto"
                role="status"
                aria-hidden="true"
            ></div>
        </div>

        <div v-else class="enqContent">
            <i v-if="disabled" class="fragment-disabled"></i>
            <form
                autocomplete="off"
                @submit.prevent="onSubmit(isOrder)"
                class="form form-inline"
                role="form"
            >
                <div class="form-group align-items-center w-100 mb-3">
                    <div class="d-block d-xl-none col-xs-12 my-1">
                        <a
                            target="_blank"
                            :href="companyRoute"
                            class="btn btn-success"
                            title="Add a new customer"
                            :class="updating || disabled ? 'disabled' : ''"
                            >{{ __("CAP") }}</a
                        >
                        <a
                            target="_blank"
                            :href="branchRoute"
                            class="btn btn-success"
                            title="Add a new branch to an existing customer"
                            :class="updating || disabled ? 'disabled' : ''"
                            >{{ __("BAP") }}</a
                        >
                        <a
                            target="_blank"
                            :href="contactRoute"
                            class="btn btn-success"
                            title="Add a new contact to an existing branch and customer"
                            :class="updating || disabled ? 'disabled' : ''"
                            >{{ __("BCAP") }}</a
                        >
                    </div>
                    <div class="d-none d-xl-block col-xl-3">
                        <a
                            target="_blank"
                            :href="companyRoute"
                            class="btn btn-success"
                            title="Add a new customer"
                            :class="updating || disabled ? 'disabled' : ''"
                            >{{ __("CAP") }}</a
                        >
                        <a
                            target="_blank"
                            :href="branchRoute"
                            class="btn btn-success"
                            title="Add a new branch to an existing customer"
                            :class="updating || disabled ? 'disabled' : ''"
                            >{{ __("BAP") }}</a
                        >
                        <a
                            target="_blank"
                            :href="contactRoute"
                            class="btn btn-success"
                            title="Add a new contact to an existing branch and customer"
                            :class="updating || disabled ? 'disabled' : ''"
                            >{{ __("BCAP") }}</a
                        >
                    </div>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <api-search
                            class="w-100"
                            :route="'enquiries/fragment/company/search'"
                            :disabled="updating || disabled"
                        >
                            <template v-slot="row">
                                <div
                                    class="dropdown-item"
                                    :class="{
                                        blacklisted: row.item.companyBlacklist,
                                        'part-exact': row.item.exact,
                                    }"
                                >
                                    <a
                                        :class="
                                            row.item.managerApproved
                                                ? ''
                                                : 'disabled'
                                        "
                                        @click="
                                            searchClick(
                                                row.item.contactID,
                                                row.defocus
                                            )
                                        "
                                    >
                                        <div class="company">
                                            {{ row.item.companyName }}
                                        </div>
                                        <div class="branchContact">
                                            <img
                                                :src="
                                                    iconPath +
                                                    '/flags/' +
                                                    row.item.code +
                                                    '.png'
                                                "
                                                :alt="row.item.name"
                                                :title="row.item.name"
                                            />
                                            {{
                                                row.item.contactName +
                                                ", " +
                                                row.item.branchName
                                            }}
                                        </div>
                                    </a>
                                </div>
                            </template>
                        </api-search>
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                >
                    <label
                        for="companyName"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Company") }}</strong>
                    </label>
                    <label
                        for="companyName"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Company") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="companyName"
                            v-model="companyName"
                            class="form-control w-100"
                            :disabled="updating || disabled"
                            :readonly="!isOrder"
                            data-lpignore="true"
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="branchName"
                >
                    <label
                        for="branchName"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Branch") }}</strong>
                    </label>
                    <label
                        for="branchName"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Branch") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="branchName"
                            v-model="branchName"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="contactName"
                >
                    <label
                        for="contactName"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Contact") }}</strong>
                    </label>
                    <label
                        for="contactName"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Contact") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="contactName"
                            v-model="contactName"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                >
                    <label
                        for="invoiceAddress"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Invoice Address") }}</strong>
                    </label>
                    <label
                        for="invoiceAddress"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Invoice Address") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <textarea
                            id="invoiceAddress"
                            v-model="invoiceAddress"
                            class="form-control w-100"
                            :disabled="updating || disabled"
                            rows="6"
                            :readonly="!isOrder"
                        ></textarea>
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                >
                    <label
                        for="deliveryAddress"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Delivery Address") }}</strong>
                    </label>
                    <label
                        for="deliveryAddress"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Delivery Address") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <textarea
                            id="deliveryAddress"
                            v-model="deliveryAddress"
                            class="form-control w-100"
                            :disabled="updating || disabled"
                            rows="6"
                            :readonly="!isOrder"
                        ></textarea>
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="'ZZ' !== enquiry.countryCode"
                >
                    <label
                        for="countryCode"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Country") }}</strong>
                    </label>
                    <label
                        for="countryCode"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Country") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="countryCode"
                            v-model="enquiry.country.name"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="branchPhone"
                >
                    <label
                        for="branchPhone"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Branch Phone Number") }}</strong>
                    </label>
                    <label
                        for="branchPhone"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Branch Phone Number") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="branchPhone"
                            v-model="branchPhone"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="contactPhoneNumber"
                >
                    <label
                        for="contactPhoneNumber"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Contact Phone Number") }}</strong>
                    </label>
                    <label
                        for="contactPhoneNumber"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Contact Phone Number") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="contactPhoneNumber"
                            v-model="contactPhoneNumber"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="mobilePhone"
                >
                    <label
                        for="mobilePhone"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Mobile Phone Number") }}</strong>
                    </label>
                    <label
                        for="mobilePhone"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Mobile Phone Number") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="mobilePhone"
                            v-model="mobilePhone"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="fax"
                >
                    <label
                        for="fax"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Fax") }}</strong>
                    </label>
                    <label
                        for="fax"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Fax") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="fax"
                            v-model="fax"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="enquiry.emailAddress"
                >
                    <label
                        for="emailAddress"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Email Address") }}</strong>
                    </label>
                    <label
                        for="emailAddress"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Email Address") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <a
                            id="emailAddress"
                            class="a-input"
                            :href="
                                'mailto:' +
                                encodeURIComponent(enquiry.emailAddress) +
                                '?subject=' +
                                enquiry.id
                            "
                            >{{ enquiry.emailAddress }}</a
                        >
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="skypeHandle"
                >
                    <label
                        for="skypeHandle"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Skype Handle") }}</strong>
                    </label>
                    <label
                        for="skypeHandle"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Skype Handle") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="skypeHandle"
                            v-model="skypeHandle"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="taxWording"
                >
                    <label
                        for="taxWording"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ taxWording }}</strong>
                    </label>
                    <label
                        for="taxWording"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ taxWording }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <a id="taxWording" class="a-input">{{ __("Yes") }}</a>
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="taxCode"
                >
                    <label
                        for="taxCode"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Tax Code") }}</strong>
                    </label>
                    <label
                        for="taxCode"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Tax Code") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="taxCode"
                            v-model="taxCode"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="currency"
                >
                    <label
                        for="currency"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Currency") }}</strong>
                    </label>
                    <label
                        for="currency"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Currency") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="currency"
                            v-model="currency"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="paymentTerms"
                >
                    <label
                        for="paymentTerms"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Payment Terms") }}</strong>
                    </label>
                    <label
                        for="paymentTerms"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Payment Terms") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <input
                            type="text"
                            autocomplete="off"
                            id="paymentTerms"
                            v-model="paymentTerms"
                            class="form-control w-100"
                            :disabled="disabled"
                            readonly
                        />
                    </div>
                </div>

                <div
                    class="form-group align-items-center mb-1 w-100"
                    v-if="bankChargesMarker"
                >
                    <label
                        for="bankChargesMarker"
                        class="d-block d-xl-none col-xs-12 control-label text-left my-1"
                    >
                        <strong>{{ __("Bank Charges Applicable") }}</strong>
                    </label>
                    <label
                        for="bankChargesMarker"
                        class="d-none d-xl-block col-xl-3 control-label text-right"
                    >
                        <strong>{{ __("Bank Charges Applicable") }}</strong>
                    </label>

                    <div class="col-xs-12 col-xl-9 w-100">
                        <a id="bankChargesMarker" class="a-input">{{
                            __("Yes")
                        }}</a>
                    </div>
                </div>

                <div
                    class="offset-xs-0 offset-xl-3 col-xs-12 col-xl-9 d-flex align-items-center w-100"
                >
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="updating || disabled"
                    >
                        {{ __("Update") }}
                    </button>

                    <div
                        class="d-flex align-items-center w-100"
                        v-if="updating"
                    >
                        <strong class="ml-4">{{ __("Updating...") }}</strong>
                        <div
                            class="spinner-border ml-auto"
                            role="status"
                            aria-hidden="true"
                        ></div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
export default {
    name: "EnqFragmentCompany",

    props: {
        enquiry: {
            required: true,
            type: Object,
        },

        disabled: {
            required: true,
            type: Boolean,
        },

        updateEnquiry: {
            required: true,
            type: Function,
        },

        iconPath: {
            required: true,
            type: String,
        },

        isOrder: {
            required: true,
            type: Boolean,
        },
    },

    data() {
        return {
            loaded: false,
            updating: false,

            vat_rate: null,

            // Stuff that's being edited
            contactID: this.enquiry.company_contact_id,
            deliveryAddress: this.enquiry.deliveryAddress,
            invoiceAddress: this.enquiry.invoiceAddress,
            companyName: this.enquiry.companyName,

            companyRoute: "",
            branchRoute: "",
            contactRoute: "",
        };
    },

    computed: {
        branchName() {
            return this.enquiry.company_contact &&
                "Head Office" !== this.enquiry.company_contact.branch.branchName
                ? this.enquiry.company_contact.branch.branchName
                : null;
        },

        branchPhone() {
            return this.enquiry.company_contact
                ? this.enquiry.company_contact.branch.phone
                : null;
        },

        bankChargesMarker() {
            return this.enquiry.company_contact
                ? this.enquiry.company_contact.branch.bankChargesMarker
                : null;
        },

        contactPhoneNumber() {
            return this.enquiry.company_contact
                ? this.enquiry.company_contact.phone
                : null;
        },

        mobilePhone() {
            return this.enquiry.company_contact
                ? this.enquiry.company_contact.mobilePhone
                : null;
        },

        fax() {
            return this.enquiry.company_contact
                ? this.enquiry.company_contact.branch.fax
                : null;
        },

        taxCode() {
            return this.enquiry.company_contact
                ? this.enquiry.company_contact.branch.taxCode
                : null;
        },

        paymentTerms() {
            return this.enquiry.company_contact &&
                this.enquiry.company_contact.branch.terms
                ? __(this.enquiry.company_contact.branch.terms.paymentTermsEN)
                : null;
        },

        skypeHandle() {
            return this.enquiry.company_contact
                ? this.enquiry.company_contact.skypeHandle
                : null;
        },

        taxWording() {
            if (
                null === this.enquiry.company_contact ||
                !this.enquiry.company_contact.branch.vatMarker
            ) {
                return null;
            }

            return "US" === this.enquiry.company_contact.branch.countryCode
                ? this.enquiry.company_contact.branch.taxPercentage +
                      "% TAX Applicable"
                : this.vat_rate * 100 + "% VAT Applicable";
        },

        contactName() {
            return this.enquiry.contactName;
        },

        currency() {
            return this.enquiry.currency
                ? this.enquiry.currency.currency +
                      " (" +
                      this.enquiry.currency.symbol +
                      ")"
                : null;
        },
    },

    methods: {
        searchClick(contactID, callback) {
            this.contactID = contactID;
            this.onSubmit();

            callback();
        },

        onSubmit(override = false) {
            this.updating = true;

            console.log("override: ", override || false);

            axios
                .post("/api/enquiries/fragment/company/update", {
                    enquiryID: this.enquiry.id,
                    contactID: this.contactID,
                    deliveryAddress: this.deliveryAddress,
                    invoiceAddress: this.invoiceAddress,
                    companyName: this.companyName,
                    override: override || false,
                })
                .then((response) => {
                    // console.log(response);
                    this.updating = false;

                    let data = response.data;
                    if (!data.success) {
                        if (data.errors) {
                            console.log(data.errors);
                        } else {
                            $("#fail-alert")
                                .text(data.message || "Unknown error")
                                .slideDown(500)
                                .delay(8000)
                                .slideUp(500);
                        }
                    } else {
                        this.contactID = data.enquiry.company_contact_id;
                        this.deliveryAddress = data.enquiry.deliveryAddress;
                        this.invoiceAddress = data.enquiry.invoiceAddress;
                        this.companyName = data.enquiry.companyName;

                        this.updateEnquiry(data.enquiry);
                    }
                })
                .catch((error) => {
                    console.error(error.response);
                    this.updating = false;

                    $("#fail-alert")
                        .text(error.toString() || "Unknown error")
                        .slideDown(500)
                        .delay(8000)
                        .slideUp(500);
                });
        },
    },

    watch: {},

    mounted() {
        axios
            .post("/api/enquiries/fragment/company/get", {
                enquiryID: this.enquiry.id,
            })
            .then((response) => {
                // console.log(response);

                let data = response.data;
                if (!data.success) {
                    $("#fail-alert")
                        .text(data.message || "Unknown error")
                        .slideDown(500)
                        .delay(5000)
                        .slideUp(500);
                } else {
                    this.loaded = true;
                    this.vat_rate = data.vat_rate;

                    this.companyRoute = data.companyRoute;
                    this.branchRoute = data.branchRoute;
                    this.contactRoute = data.contactRoute;
                }
            })
            .catch((error) => {
                console.error(error.response);

                $("#fail-alert")
                    .text(error.toString() || "Unknown error")
                    .slideDown(500)
                    .delay(5000)
                    .slideUp(500);
            });
    },
};
</script>
